import {
  CANCELLED_TRIP_STATUS_CODES,
  COVERAGE_LIMITED_TRIP_TYPE_CODES,
  TRIP_STATUS_CODES,
} from '@/constants';

/**
 * Generates a trip query object from a given trip grouping, member UUID, and coverage program identification number.
 *
 * @param {Object} tripGrouping - The trip grouping object containing static and dynamic attributes.
 * @param {string} memberUuid - The UUID of the member for whom the trip query is being generated.
 * @param {string} coverageProgramIdentificationNumber - The coverage program identification number for the member.
 * @param {Array} requestedPickupDateTimeRange - The date/time range for which the trip grouping should query
 * @returns {Object} - The generated trip query object.
 */
export const getTripQueryFromTripGrouping = (
  tripGrouping,
  memberUuid,
  coverageProgramIdentificationNumber,
  requestedPickupDateTimeRange,
) => {
  const { statuses, ...restOfStaticGroupByAttributes } =
    tripGrouping.staticGroupByAttributes ?? {};

  // A cancelled trip never took place, so it cannot consume a member's coverage
  // allotment. Groupings configure which statuses they count, but that configuration
  // must not be able to opt into counting cancellations — so filter them out here
  // rather than relying on every grouping being configured correctly.
  //
  // When a grouping configures no statuses at all the query would otherwise match
  // every status, cancellations included; fall back to the full list so the exclusion
  // still applies.
  const countedStatuses = (
    statuses?.length ? statuses : Object.values(TRIP_STATUS_CODES)
  ).filter((status) => !CANCELLED_TRIP_STATUS_CODES.has(status));

  const tripQuery = {
    ...restOfStaticGroupByAttributes,
    statuses: countedStatuses,
    requestedTripTypes: COVERAGE_LIMITED_TRIP_TYPE_CODES,
    limit: 1,
    page: 1,
    fields: 'uuid',
  };

  // Populate dynamic group-by attributes and translate to trip query

  // Patient identifier parameter
  switch (tripGrouping.dynamicGroupByAttributes?.patientIdentifier) {
    case 'uuid':
      tripQuery.patientUuid = memberUuid;
      break;
    default:
      break;
  }

  // Date/time of service range parameter
  switch (tripGrouping.dynamicGroupByAttributes?.dateTimeOfServiceRange) {
    case 'CALENDAR_YEAR':
      tripQuery.requestedPickupDateTimeRange = requestedPickupDateTimeRange;
      break;
    default:
      break;
  }

  switch (tripGrouping.dynamicGroupByAttributes?.electedEnrollmentIdentifier) {
    case 'coverageProgramIdentificationNumber':
      tripQuery.electedEnrollmentCoverageProgramIdentificationNumber =
        coverageProgramIdentificationNumber;
      break;
    default:
      break;
  }

  return tripQuery;
};
